import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, map, Observable, of} from "rxjs";
import * as _ from "lodash";

import {ParametersService} from "./parameters.service";
import {SubtitlesService} from "./subtitles.service";
import {UserService} from "./user.service";
import {
    SingleModuleData,
    Tile,
    TileContainer,
    TileOrder,
    TilesData, ZonesData, ZonesTileData
} from "../models";

@Injectable({
    providedIn: 'root'
})
export class DatabaseService {
    tiles = new BehaviorSubject<TilesData[] | ZonesTileData[] | {}>({});
    tiles$ = this.tiles.asObservable();
    tilesOrder: TileOrder[] = [];
    zones = new BehaviorSubject<ZonesData | {}>({});
    zonesResponse = new BehaviorSubject<SingleModuleData | null>(null);
    lastUpdate: any = null;

    constructor(
        private http: HttpClient,
        private subtitles: SubtitlesService,
        private parameters: ParametersService,
        private user: UserService
    ) {
    }

    getModuleData(): Observable<SingleModuleData> {
        if (this.parameters.selectedModuleHash === 0) return of();

        return this.http.get<SingleModuleData>(`/api/v1/users/${this.user.user_id}/modules/${this.parameters.selectedModuleHash}`)
            .pipe(map((response): SingleModuleData => {
                    if (response.zones.transaction_time === null) {
                        this.lastUpdate = response.tilesLastUpdate;
                    } else {
                        this.lastUpdate = response.zones.transaction_time;
                    }

                    response.zones.elements = response.zones.elements.filter((zone: any) => zone !== null);

                    if ((this.parameters.demoAccount()) && Object.keys(this.zones).length > 0) {
                        const zonesNames = [1984, 1985, 1986, 1987, 1988, 1989, 1990, 1991];

                        response.zones.elements.forEach((el: any, i: number) => {
                            el.description.name = this.subtitles.setSubtitle(zonesNames[i])
                        });
                    }

                    this.zones.next(response.zones);

                    if (response.tilesOrder !== null) {
                        this.tilesOrder = this.createTilesOrder(response.tilesOrder);
                        this.tiles.next(response.tiles.map((tile: any) => this.setTileOrder(tile)));
                    } else {
                        this.tilesOrder = [];
                        this.tiles.next(response.tiles);
                    }

                    const zonesResponseCopy = _.cloneDeep(response);
                    this.zonesResponse.next(zonesResponseCopy);

                    return response;
                })
            );
    }

    updateTilesOrder(order: string | null): void {
        this.tilesOrder = order === null ? [] : this.createTilesOrder(order);

        const tilesCopy = _.cloneDeep(this.tiles.getValue()) as TilesData[];
        const updatedTilesCopy = tilesCopy.map((tile: any) =>
            order === null ? this.setTileOrder(tile, null) : this.setTileOrder(tile)
        );

        this.tiles.next(order === null
            ? updatedTilesCopy.sort(this.sortTilesByTypeAndId)
            : updatedTilesCopy);
    }

    setTileOrder(tile: TilesData, externalTilesOrder?: TileOrder[]): TilesData {
        if (tile.type === 'alarm' || tile.type === 'clear_alarm') {
            return tile;
        }

        let order: TileOrder;

        if (externalTilesOrder) {
            order = externalTilesOrder.find(order => order.id === tile.id);
        }
        if (externalTilesOrder === undefined) {
            order = this.tilesOrder.find(order => order.id === tile.id);
        }

        let newOrderId = order ? order.orderId : tile.orderId;
        let newVisibility = order !== undefined ? order.visibility : tile.visibility

        // it removes tile order after set default order in settings
        if (externalTilesOrder === null) {
            newOrderId = null;
            newVisibility = true;
        }

        const updatedTile: Tile | TileContainer = {
            ...tile,
            orderId: newOrderId,
            visibility: newVisibility,
        }

        if (tile.type === 60) {
            const updatedContainerTile = updatedTile as TileContainer;
            updatedContainerTile.expanded = order !== undefined ? order.expanded : false;
            return updatedContainerTile;
        }

        return updatedTile;
    }

    createTilesOrder(orderString: string): any {
        let containerOrder = 0;
        let tileOrder = 0

        return orderString
            .split(',')
            .filter(str => str.length > 0)
            .map(chunk => {
                const rawOrder = chunk
                    .split('|')
                    .map(num => parseInt(num, 10));

                const order: TileOrder = {
                    id: rawOrder[0],
                    visibility: rawOrder[1] === 1,
                    expanded: false,
                    orderId: null,
                };

                if (rawOrder.length === 3) { // container
                    order.expanded = rawOrder[2] === 1;
                    containerOrder += 1;
                    order.orderId = containerOrder;
                    tileOrder = 0;
                } else { // tile
                    tileOrder += 1;
                    order.orderId = tileOrder;
                }

                return order;
            });
    }

    sortTilesByTypeAndId = (a: TilesData, b: TilesData): number => {
        if (a.type < b.type)
            return -1;
        if (a.type > b.type)
            return 1;
        if (a.id < b.id)
            return -1;
        if (a.id > b.id)
            return 1;
        return 0;
    }

    resetDatabaseService(): void {
        this.tiles.next({});
        this.tilesOrder = [];
        this.zones.next({});
        this.lastUpdate = null;
    }

    hasZonesData(): boolean {
        const zonesData = this.zones.getValue();
        return Object.keys(zonesData).length !== 0;
    }

    getZoneTiles(): ZonesTileData[] {
        const tilesData = this.tiles.getValue();
        if (Array.isArray(tilesData) && this.parameters.isZonesOrZonesSystemControllerType()) {
            return tilesData as ZonesTileData[];
        }
        return [];
    }

    isZonesDataType(obj: unknown): obj is ZonesData {
        return (
            typeof obj === 'object' &&
            obj !== null &&
            'elements' in obj &&
            Array.isArray((obj as ZonesData).elements)
        );
    }
}
